import { inject, Injectable, InjectionToken } from '@angular/core';
import { BazisSrvService } from '@bazis/shared/services/srv.service';
import { debounceTime, map, tap } from 'rxjs/operators';
import { of, shareReplay, switchMap, combineLatest, withLatestFrom } from 'rxjs';
import { BazisConfigurationService, SHARE_REPLAY_SETTINGS } from '@bazis/configuration.service';
import { TemplateObservable } from '@bazis/shared/classes/template-observable';
import { BazisAuthService } from '@bazis/shared/services/auth.service';

/**
 * Deprecated injection token for full search models list.
 *
 * Prefer using `BazisConfigurationService.allSearchModels` instead.
 * When provided, the value should be an array of model objects with at least `{ id: string }`.
 */
export const ALL_SEARCH_MODELS_DATA = new InjectionToken<any[]>('', {
    providedIn: 'root',
    factory: () => null,
});

/**
 * Deprecated injection token for role -> model ids mapping.
 *
 * Prefer using `BazisConfigurationService.roleSearchModelIds` instead.
 * When provided, the value should map role slug to an array of model ids.
 */
export const ROLE_SEARCH_MODEL_IDS_DATA = new InjectionToken<{ [index: string]: any }>('', {
    providedIn: 'root',
    factory: () => null,
});

/**
 * **BazisSearchService**
 *
 * Global search service.
 * Keeps search query, selected model and page in reactive state, requests backend search results,
 * filters results by available models for the current role, and provides pagination helpers.
 */
@Injectable({
    providedIn: 'root',
})
export class BazisSearchService {
    protected srv = inject(BazisSrvService);

    protected authService = inject(BazisAuthService);

    protected configurationService = inject(BazisConfigurationService);

    public allSearchModels = inject(ALL_SEARCH_MODELS_DATA); // deprecated, need to be replaced by allSearchModels option in ConfigService

    public roleModelIds = inject(ROLE_SEARCH_MODEL_IDS_DATA); // deprecated, need to be replaced by roleSearchModelIds option in ConfigService

    /** Current search text entered by user. */
    search = new TemplateObservable('');

    /** Current selected model id (`'all'` by default). */
    model = new TemplateObservable('all');

    /** Current page index (0-based). */
    page = new TemplateObservable(0);

    /** Loading indicator holder used by UI. */
    isLoading = new TemplateObservable(false);

    /** Page size for `pageResults$`. */
    private _pageLimit = 20;

    /** Debounce time for search requests in milliseconds. */
    private _debounceTime = 500;

    /**
     * Available models per role mapping.
     * Source priority: configuration service -> deprecated token -> empty object.
     */
    availableRoleModelIds = this.configurationService.roleSearchModelIds || this.roleModelIds || {};

    /**
     * Search models map by id.
     * Source priority: configuration service -> deprecated token -> empty array.
     */
    searchModelsMap = (
        this.configurationService.allSearchModels ||
        this.allSearchModels ||
        []
    ).reduce((acc, current) => {
        acc[current.id] = current;
        return acc;
    }, {});

    /**
     * Models list per role, built from `availableRoleModelIds` and `searchModelsMap`.
     * Shape: `{ [roleSlug: string]: any[] }`.
     */
    roleModels = Object.keys(this.availableRoleModelIds).reduce((acc, current) => {
        acc[current] = this.availableRoleModelIds[current].map(
            (model) => this.searchModelsMap[model],
        );
        return acc;
    }, {});

    /**
     * Models available for current user role.
     *
     * Resets selection (`model='all'`, `page=0`) on role change.
     *
     * @returns Observable of models array.
     */
    searchModels$ = this.authService.role$.pipe(
        tap((v) => {
            this.model.set('all');
            this.page.set(0);
        }),
        map((role) => this.roleModels[role]),
        shareReplay(SHARE_REPLAY_SETTINGS),
    );

    /**
     * Whether search is available for current role (models list is non-empty).
     *
     * @returns Observable of boolean.
     */
    canSearch$ = this.searchModels$.pipe(
        map((models) => models.length > 0),
        shareReplay(SHARE_REPLAY_SETTINGS),
    );

    /**
     * Full search results stream.
     *
     * Requests `common/search` endpoint with:
     * - `query`
     * - optional `model_name` when selected model is not `'all'`
     *
     * Side effects:
     * - sets `isLoading`
     * - resets page to 0 after response
     *
     * Results are filtered to models allowed for the current role.
     *
     * @returns Observable of filtered results array.
     */
    results$ = combineLatest([this.model.$, this.search.$]).pipe(
        tap(() => {
            this.isLoading.set(true);
        }),
        debounceTime(this._debounceTime),
        switchMap(([model, search]) => {
            const params: any = { query: search };
            if (model !== 'all') params.model_name = model;
            return search
                ? this.srv.commonGetRequest$('common/search', params)
                : of({ result: [] });
        }),
        tap((r) => {
            this.setPage(0);
            this.isLoading.set(false);
        }),
        withLatestFrom(this.searchModels$),

        map(([response, searchModels]) =>
            this._filterResultsByModels(searchModels, response.result),
        ),
        shareReplay(SHARE_REPLAY_SETTINGS),
    );

    /**
     * Short results list (first 2 items).
     *
     * @returns Observable of results slice.
     */
    shortListResults$ = this.results$.pipe(
        map((v) => v.slice(0, 2)),
        shareReplay(SHARE_REPLAY_SETTINGS),
    );

    /**
     * Current page results slice.
     *
     * @returns Observable of page slice (size `_pageLimit`).
     */
    pageResults$ = combineLatest([this.results$, this.page.$]).pipe(
        map(([results, page]) =>
            results.slice(page * this._pageLimit, (page + 1) * this._pageLimit),
        ),
        shareReplay(SHARE_REPLAY_SETTINGS),
    );

    /**
     * Per-model result counts for UI panels.
     *
     * Uses `srv.groupRequest$()` to request results for each model in parallel
     * and then counts filtered results per model.
     *
     * @returns Observable of `{ id, count }[]`.
     */
    panelCount$ = combineLatest([this.search.$, this.searchModels$]).pipe(
        debounceTime(this._debounceTime),
        switchMap(([search, searchModels]) => {
            return search
                ? this.srv.groupRequest$(
                      searchModels.map((model) => {
                          const params: any = { query: search };
                          if (model.id !== 'all') params.model_name = model.id;

                          return {
                              entityType: 'common.search',
                              requestType: 'any',
                              meta: [],
                              params,
                              method: 'GET',
                          };
                      }),
                  )
                : of(searchModels.map((model) => ({ result: [] })));
        }),
        withLatestFrom(this.searchModels$),
        map(([response, searchModels]) => {
            return searchModels.map((model, index) => {
                return {
                    id: model.id,
                    count:
                        this._filterResultsByModels(searchModels, response[index].result).length ||
                        0,
                };
            });
        }),
        shareReplay(SHARE_REPLAY_SETTINGS),
    );

    /**
     * Pagination data for the current results set.
     *
     * @returns Observable of `{ offset, limit, count }`.
     */
    pagination$ = combineLatest([this.results$, this.page.$]).pipe(
        map(([results, page]) => {
            return {
                offset: page * this._pageLimit,
                limit: this._pageLimit,
                count: results.length,
            };
        }),
    );

    /**
     * @param srv - API service used to query backend search endpoints.
     * @param authService - Auth service used to get current role.
     * @param configurationService - Configuration source for search models.
     * @param allSearchModels - Deprecated injected models list.
     * @param roleModelIds - Deprecated injected role -> model ids mapping.
     */
    /**
     * Set selected search model id.
     *
     * @param id - Model id string.
     * @returns void
     */
    setModel(id) {
        this.model.set(id);
    }

    /**
     * Set current page index.
     *
     * @param page - Page index (0-based).
     * @returns void
     */
    setPage(page) {
        this.page.set(page);
    }

    /**
     * Set search text.
     *
     * @param text - Search query string.
     * @returns void
     */
    setSearch(text) {
        this.search.set(text);
    }

    /**
     * Filter backend results to only allowed models and normalize description field.
     *
     * Description is built from `query_data` values joined by comma.
     *
     * @param models - Allowed models list.
     * @param results - Raw backend results list.
     * @returns Filtered and normalized results list.
     */
    protected _filterResultsByModels(models, results) {
        const modelsMap = models.reduce((acc, current) => {
            acc[current.id] = true;
            return acc;
        }, {});
        return results
            .filter((v) => modelsMap[v.model_name])
            .map((item) => {
                return {
                    ...item,
                    description: Object.values(item.query_data).join(', '),
                };
            });
    }

    /**
     * Reset search state to initial values.
     *
     * @returns void
     */
    reset() {
        this.setPage(0);
        this.setSearch('');
        this.setModel('all');
    }
}
